import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/seo";
import VinylCard from "../components/VinylCard";

const PER_PAGE = 24;

export default function GenrePage() {
  const { style: rawStyle } = useParams();
  const style = decodeURIComponent(rawStyle || "");
  const { t } = useI18n();
  const [data, setData] = useState({ items: [], pagination: { total: 0, pages: 1, page: 1 } });
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || 1);

  useSEO({
    title: `${style} · vinilos en Maggie Records`,
    description: `Catálogo de vinilos del estilo ${style} disponibles en Maggie Records. Cada disco enlaza al listing real en Discogs Marketplace.`,
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  useEffect(() => {
    setLoading(true);
    api.get("/listings", { params: { style, per_page: PER_PAGE, page } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], pagination: { total: 0, pages: 1, page: 1 } }))
      .finally(() => setLoading(false));
  }, [style, page]);

  const goPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p > 1) next.set("page", String(p)); else next.delete("page");
    setSearchParams(next);
  };

  const intro = useMemo(() => {
    return `Vinilos del estilo ${style} disponibles ahora mismo en Maggie Records. Cada disco enlaza al listing real en Discogs Marketplace.`;
  }, [style]);

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="genre-page">
      <p className="text-xs text-muted-foreground mb-3">
        <Link to="/" className="hover:text-foreground">Inicio</Link>
        {" · "}
        <Link to="/catalogo" className="hover:text-foreground">Catálogo</Link>
        {" · "}
        <span>{style}</span>
      </p>
      <div className="border-b border-border pb-6 md:pb-8 mb-8 md:mb-10">
        <h1 className="text-3xl md:text-5xl font-medium leading-tight" data-testid="genre-title">
          {style}
        </h1>
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl leading-relaxed">
          {intro}
        </p>
        <p className="text-xs text-muted-foreground mt-3">
          {data.pagination.total} {data.pagination.total === 1 ? "disco" : "discos"}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-square bg-[hsl(var(--muted))] animate-pulse" />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <div className="py-24 text-center border border-dashed border-border" data-testid="genre-empty">
          <p className="text-sm text-muted-foreground">{t("catalog.empty")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10" data-testid="genre-grid">
          {data.items.map((it) => (
            <VinylCard key={it.listing_id} item={it} />
          ))}
        </div>
      )}

      {data.pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-12 border-t border-border pt-5 text-sm">
          <button
            onClick={() => goPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="disabled:opacity-30 text-muted-foreground hover:text-foreground"
            data-testid="genre-prev"
          >
            {t("catalog.prev")}
          </button>
          <span className="text-muted-foreground text-xs">
            {t("catalog.page")} {page} / {data.pagination.pages}
          </span>
          <button
            onClick={() => goPage(Math.min(data.pagination.pages, page + 1))}
            disabled={page >= data.pagination.pages}
            className="disabled:opacity-30 text-muted-foreground hover:text-foreground"
            data-testid="genre-next"
          >
            {t("catalog.next")}
          </button>
        </div>
      )}
    </div>
  );
}
